import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronRight, Info } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { api } from "@/lib/api";
import { COUNTRIES, CA_PROVINCES } from "@/data/geography";
import { cartApi, useCart } from "@/lib/cart";

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export const itemLine = (it) =>
  it.type === "plar"
    ? { name: "PLAR assessment", amount: 200 }
    : it.type === "extension"
      ? { name: `Course extension — ${it.months} month${it.months > 1 ? "s" : ""}${it.course_name ? ` (${it.course_name})` : ""}`, amount: 70 * it.months }
      : { name: `Course transfer (${it.from_course} → ${it.to_course})`, amount: 150 };

export default function Checkout() {
  const { admin, clear } = useCart();
  const items = admin;
  // A PLAR assessment needs the full student picture (transcripts review);
  // extension/transfer purchases only need contact details.
  const needsDetails = items.some((i) => i.type === "plar");
  const [form, setForm] = useState({
    student_name: "",
    email: "",
    phone: "",
    grade_level: "",
    student_type: "ontario",
    parent_name: "",
    parent_email: "",
    address: "",
    country: "Canada",
    province: "Ontario",
    school_board: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [refId, setRefId] = useState(null);
  const [paid, setPaid] = useState(0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setType = (v) =>
    setForm((f) => ({
      ...f,
      student_type: v,
      country: v === "international" ? "" : "Canada",
      province: v === "ontario" ? "Ontario" : "",
    }));

  const fees = items.map(itemLine);
  const total = fees.reduce((t, f) => t + f.amount, 0);

  const submit = async () => {
    if (!form.student_name.trim() || !emailOk(form.email) || !form.phone.trim()) {
      toast.error("Please add the student name, a valid email and a phone number.");
      return;
    }
    if (!form.parent_name.trim() || !emailOk(form.parent_email)) {
      toast.error("Please add the parent/guardian name and a valid email.");
      return;
    }
    if (needsDetails) {
      if (!form.grade_level) {
        toast.error("Please choose the grade level.");
        return;
      }
      if (!form.address.trim() || !form.country) {
        toast.error("Please add your address and country.");
        return;
      }
      if (form.country === "Canada" && !form.province) {
        toast.error("Please choose your province.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.post("/service-orders", { ...form, items });
      setPaid(total); // snapshot before clear() empties the cart
      setRefId(res.data.ref_id);
      clear();
      toast.success("Order received — welcome to Eletech!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (refId) {
    return (
      <section className="container-x flex min-h-[70vh] items-center justify-center py-20" data-testid="checkout-success">
        <Reveal>
          <div className="card-line max-w-lg p-10 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-aqualight text-aqua">
              <Check className="h-7 w-7" />
            </span>
            <h2 className="mt-6 font-display text-2xl font-extrabold text-ink">Order received, {form.student_name.split(" ")[0]}.</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid="checkout-success-total">
              Your service order (${paid}) has been received. Save this reference — our team will email {form.email} within
              two business days with your invoice and next steps.
            </p>
            <p className="mt-4 rounded-xl border border-brand/30 bg-brandlight/70 p-4 text-left text-sm font-medium leading-relaxed text-ink" data-testid="checkout-docs-note">
              We'll reach out to collect the required documents over the email id you've given. Please ensure to provide acceptable documents within 7 days.
            </p>
            <p className="mt-6 rounded-xl border border-brand/30 bg-brandlight px-6 py-4 font-mono text-2xl font-bold tracking-wider text-ink" data-testid="checkout-reference">
              {refId}
            </p>
          </div>
        </Reveal>
      </section>
    );
  }

  if (!items.length) {
    return (
      <>
        <PageHero
          eyebrow="Service Checkout"
          lines={[<>Administrative fees,</>, <span className="font-serif italic font-medium text-brand">in two minutes</span>]}
          desc="Pay for PLAR assessments, course transfers and course extensions without re-applying."
          testid="checkout-page-heading"
        />
        <section className="container-x py-20" data-testid="checkout-empty">
          <Reveal>
            <div className="card-line mx-auto max-w-lg p-10 text-center">
              <p className="font-display text-lg font-bold text-ink">Your cart is empty.</p>
              <p className="mt-2 text-sm text-slate-500">Add a service from the Administrative Fees list on the Tuition page, or register for credits on the Apply page.</p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/tuition" data-testid="checkout-empty-tuition" className="btn-primary">Tuition &amp; fees</Link>
                <Link to="/admissions" data-testid="checkout-empty-apply" className="btn-ghost">Apply for credits</Link>
              </div>
            </div>
          </Reveal>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Service Checkout"
        lines={[<>Administrative fees,</>, <span className="font-serif italic font-medium text-brand">in two minutes</span>]}
        desc="Pay for PLAR assessments, course transfers and course extensions without re-applying."
        testid="checkout-page-heading"
      />
      <section className="container-x py-20 sm:py-28" data-testid="checkout-form-section">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="card-line p-6 sm:p-10" data-testid="checkout-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="field-label">Student full name *</label>
                  <input value={form.student_name} onChange={(e) => set("student_name", e.target.value)} className="field" placeholder="Jordan Meyer" data-testid="checkout-name-input" />
                </div>
                <div>
                  <label className="field-label">Student email *</label>
                  <input value={form.email} onChange={(e) => set("email", e.target.value)} className="field" placeholder="you@email.com" type="email" data-testid="checkout-email-input" />
                </div>
                <div>
                  <label className="field-label">Phone *</label>
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="field" placeholder="+1 (555) 000-0000" data-testid="checkout-phone-input" />
                </div>

                <div>
                  <label className="field-label">Parent/Guardian name *</label>
                  <input value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} className="field" placeholder="Alex Meyer" data-testid="checkout-parent-name-input" />
                </div>
                <div>
                  <label className="field-label">Parent/Guardian email *</label>
                  <input value={form.parent_email} onChange={(e) => set("parent_email", e.target.value)} className="field" placeholder="parent@email.com" type="email" data-testid="checkout-parent-email-input" />
                </div>

                {needsDetails && (
                  <>
                    <p className="sm:col-span-2 mt-2 flex items-start gap-2 rounded-xl bg-brandlight/60 p-4 text-xs leading-relaxed text-slate-600" data-testid="checkout-plar-note">
                      <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-brand" />
                      A PLAR assessment reviews your previous transcripts, so we need the full student details — just like on the application form.
                    </p>
                    <div>
                      <label className="field-label">Student type *</label>
                      <select value={form.student_type} onChange={(e) => setType(e.target.value)} className="field" data-testid="checkout-type-select">
                        <option value="ontario">Ontario based</option>
                        <option value="out_of_province">Out-of-province (Canada)</option>
                        <option value="international">International Students</option>
                      </select>
                    </div>
                    <div>
                      <label className="field-label">Current School Grade Level *</label>
                      <select value={form.grade_level} onChange={(e) => set("grade_level", e.target.value)} className="field" data-testid="checkout-grade-select">
                        <option value="">Choose a grade…</option>
                        <option value="9">Entering Grade 9</option>
                        <option value="10">Entering Grade 10</option>
                        <option value="11">Entering Grade 11</option>
                        <option value="12">Entering Grade 12</option>
                        <option value="homeschool">Homeschool</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label">Address *</label>
                      <input value={form.address} onChange={(e) => set("address", e.target.value)} className="field" placeholder="354 Rymal Rd E" data-testid="checkout-address-input" />
                    </div>
                    <div>
                      <label className="field-label">School Board</label>
                      <input value={form.school_board} onChange={(e) => set("school_board", e.target.value)} className="field" placeholder="e.g. Hamilton-Wentworth DSB" data-testid="checkout-school-board-input" />
                    </div>
                    <div>
                      <label className="field-label">Country *</label>
                      {form.student_type === "international" ? (
                        <select value={form.country} onChange={(e) => set("country", e.target.value)} className="field" data-testid="checkout-country-select">
                          <option value="">Choose a country…</option>
                          {COUNTRIES.filter((c) => c !== "Canada").map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      ) : (
                        <select value="Canada" disabled className="field opacity-80" data-testid="checkout-country-select">
                          <option value="Canada">Canada</option>
                        </select>
                      )}
                    </div>
                    {form.student_type === "international" ? (
                      <div>
                        <label className="field-label">Province / State</label>
                        <input value={form.province} onChange={(e) => set("province", e.target.value)} className="field" placeholder="Your province or state" data-testid="checkout-province-input" />
                      </div>
                    ) : form.student_type === "out_of_province" ? (
                      <div>
                        <label className="field-label">Province *</label>
                        <select value={form.province} onChange={(e) => set("province", e.target.value)} className="field" data-testid="checkout-province-select">
                          <option value="">Choose a province…</option>
                          {CA_PROVINCES.filter((p) => p !== "Ontario").map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="field-label">Province *</label>
                        <select value="Ontario" disabled className="field opacity-80" data-testid="checkout-province-select">
                          <option value="Ontario">Ontario</option>
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-8 rounded-2xl border border-brand/30 bg-brandlight/60 p-6" data-testid="checkout-fee-summary">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand">Fee summary</p>
                <div className="mt-3 space-y-2">
                  {fees.map((f) => (
                    <p key={f.name} className="flex items-start justify-between gap-6 text-sm text-slate-600" data-testid="checkout-fee-line">
                      <span>{f.name}</span>
                      <strong className="text-ink">${f.amount}</strong>
                    </p>
                  ))}
                  <p className="flex items-center justify-between gap-6 border-t border-brand/30 pt-3">
                    <strong className="text-sm text-ink">Total</strong>
                    <strong className="font-mono text-xl text-brand" data-testid="checkout-total">${total}</strong>
                  </p>
                </div>
              </div>

              <button onClick={submit} disabled={submitting} data-testid="checkout-submit-btn" className="btn-accent mt-8 w-full">
                {submitting ? "Submitting…" : <><ChevronRight className="h-4 w-4" /> Submit <span className="text-ink">order</span></>}
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
